import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getServicesManager } from '../../../services'
import { MegaPromptComposer, SplitComposer, SplitGoal, SplitComposersResponse } from '../../../interfaces/bot-config'
import { PayloadAction } from '@reduxjs/toolkit'

// Async thunks
export const fetchMegaComposers = createAsyncThunk(
  'megaComposers/fetchMegaComposers',
  async () => {
    const servicesManager = getServicesManager()
    const composers = await servicesManager.getMegaComposer()
    return composers || []
  }
)

export const fetchSplitComposers = createAsyncThunk(
  'prompts/fetchSplitComposers',
  async (_, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.getSplitComposers()
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch split composers')
    }
  }
)

export const createSplitComposer = createAsyncThunk(
  'prompts/createSplitComposer',
  async (data: { composer_name: string, state_id: string, goals?: SplitGoal[] }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.createSplitComposer(data)
      if (response === null) {
        return rejectWithValue('Failed to create split composer - please check your input and try again')
      }
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create split composer')
    }
  }
)

export const updateSplitComposer = createAsyncThunk(
  'prompts/updateSplitComposer',
  async ({ composerId, data }: { composerId: string, data: { composer_name?: string, state_id?: string, goals?: SplitGoal[] } }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.updateSplitComposer(composerId, data)
      if (response === null) {
        return rejectWithValue('Failed to update split composer - please check your input and try again')
      }
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update split composer')
    }
  }
)

export const deleteSplitComposer = createAsyncThunk(
  'prompts/deleteSplitComposer',
  async (composerId: string, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      await servicesManager.deleteSplitComposer(composerId)
      return composerId
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete split composer')
    }
  }
)

// State interfaces
interface MegaComposersState {
  composers: MegaPromptComposer[]
  isLoading: boolean
  error: string | null
}

interface SplitComposersState {
  composers: SplitComposer[]
  goals: SplitGoal[]
  isLoading: boolean
  error: string | null
}

interface PromptsState {
  megaComposers: MegaComposersState
  splitComposers: SplitComposersState
}

const initialState: PromptsState = {
  megaComposers: {
    composers: [],
    isLoading: false,
    error: null,
  },
  splitComposers: {
    composers: [],
    goals: [],
    isLoading: false,
    error: null,
  }
}

// Slice
const promptsSlice = createSlice({
  name: 'prompts',
  initialState,
  reducers: {
    clearMegaErrors: (state) => {
      state.megaComposers.error = null
    },
    clearSplitErrors: (state) => {
      state.splitComposers.error = null
    },
    clearMegaComposers: (state) => {
      state.megaComposers.composers = []
      state.megaComposers.error = null
    },
    clearSplitComposers: (state) => {
      state.splitComposers.composers = []
      state.splitComposers.goals = []
      state.splitComposers.error = null
    },
    addMegaComposer: (state, action: PayloadAction<MegaPromptComposer>) => {
      state.megaComposers.composers.push(action.payload)
    },
    updateMegaComposer: (state, action: PayloadAction<MegaPromptComposer>) => {
      const index = state.megaComposers.composers.findIndex(composer => composer.composer_id === action.payload.composer_id)
      if (index !== -1) {
        state.megaComposers.composers[index] = action.payload
      }
    },
    deleteMegaComposer: (state, action: PayloadAction<string>) => {
      state.megaComposers.composers = state.megaComposers.composers.filter(composer => composer.composer_id !== action.payload)
    },
  },
  extraReducers: (builder) => {
    // Mega Composers
    builder
      .addCase(fetchMegaComposers.pending, (state) => {
        state.megaComposers.isLoading = true
        state.megaComposers.error = null
      })
      .addCase(fetchMegaComposers.fulfilled, (state, action) => {
        state.megaComposers.isLoading = false
        state.megaComposers.composers = action.payload
      })
      .addCase(fetchMegaComposers.rejected, (state, action) => {
        state.megaComposers.isLoading = false
        state.megaComposers.error = action.error.message || 'Failed to fetch mega composers'
      })

    // Split Composers
    builder
      .addCase(fetchSplitComposers.pending, (state) => {
        state.splitComposers.isLoading = true
        state.splitComposers.error = null
      })
      .addCase(fetchSplitComposers.fulfilled, (state, action) => {
        state.splitComposers.isLoading = false
        const payload = action.payload as SplitComposersResponse
        state.splitComposers.composers = payload.composers || []
        state.splitComposers.goals = payload.goals || []
      })
      .addCase(fetchSplitComposers.rejected, (state, action) => {
        state.splitComposers.isLoading = false
        state.splitComposers.error = action.payload as string
      })
      .addCase(createSplitComposer.pending, (state) => {
        state.splitComposers.error = null
      })
      .addCase(createSplitComposer.fulfilled, (state, action) => {
        if (action.payload) {
          state.splitComposers.composers.push(action.payload)
        }
      })
      .addCase(createSplitComposer.rejected, (state, action) => {
        state.splitComposers.error = action.payload as string
      })
      .addCase(updateSplitComposer.pending, (state) => {
        state.splitComposers.error = null
      })
      .addCase(updateSplitComposer.fulfilled, (state, action) => {
        // Update goals in Redux state directly from the payload
        const { composerId, data } = action.meta.arg
        
        // Update composer info if provided
        if (data.composer_name || data.state_id) {
          const composerIndex = state.splitComposers.composers.findIndex(c => c.composer_id === composerId)
          if (composerIndex !== -1) {
            if (data.composer_name) state.splitComposers.composers[composerIndex].composer_name = data.composer_name
            if (data.state_id) state.splitComposers.composers[composerIndex].state_id = data.state_id
          }
        }
        
        // Update goals if provided (including empty array)
        if (data.goals !== undefined) {
          // Remove old goals for this composer
          state.splitComposers.goals = state.splitComposers.goals.filter(
            goal => goal.composer_id !== composerId
          )
          
          // Add new goals if any (convert from payload format to SplitGoal format)
          if (data.goals.length > 0) {
            const newGoals = data.goals.map((goal: any, index: number) => ({
              ...goal,
              composer_id: composerId,
              order: index
            }))
            
            state.splitComposers.goals.push(...newGoals)
          }
          // If data.goals is empty array, we just removed old goals and don't add any new ones
        }
      })
      .addCase(updateSplitComposer.rejected, (state, action) => {
        state.splitComposers.error = action.payload as string
      })
      .addCase(deleteSplitComposer.pending, (state) => {
        state.splitComposers.error = null
      })
      .addCase(deleteSplitComposer.fulfilled, (state, action) => {
        const deletedComposerId = action.payload as string
        state.splitComposers.composers = state.splitComposers.composers.filter(
          composer => composer.composer_id !== deletedComposerId
        )
        state.splitComposers.goals = state.splitComposers.goals.filter(
          goal => goal.composer_id !== deletedComposerId
        )
      })
      .addCase(deleteSplitComposer.rejected, (state, action) => {
        state.splitComposers.error = action.payload as string
      })
  },
})

export const { 
  clearMegaErrors, 
  clearSplitErrors, 
  clearMegaComposers, 
  clearSplitComposers, 
  addMegaComposer, 
  updateMegaComposer, 
  deleteMegaComposer 
} = promptsSlice.actions

// Legacy exports for backward compatibility
export const clearError = clearMegaErrors
export const clearComposers = clearMegaComposers
export const addComposer = addMegaComposer
export const updateComposer = updateMegaComposer
export const deleteComposer = deleteMegaComposer

export default promptsSlice.reducer 